import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Icon from '@ant-design/icons';
import type { CustomIconComponentProps } from '@ant-design/icons/lib/components/Icon';
import { captureException } from '@sentry/react';
import type { MenuProps } from 'antd';
import { Avatar, Divider, Dropdown, Modal, Tag, Typography } from 'antd';
import { TbCloudLock, TbInfoCircle, TbMailSpark, TbUserScan } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { workspaceService } from '@/services/database';
import { DropdownMenuPanel } from '@/shared';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { toast } from '@/shared/components/toasts/PlatformToast';
import SmallTooltip from '@/shared/components/tooltips/SmallTooltip';
import { useWorkspaceStore } from '@/stores';
import { useUserStore } from '@/stores/database/entities/00-RBAC/01-Users/store';
import { WorkspaceAuthorizedAuthenticationMethods } from '@/types';
import { SessionLoginMethod } from '@/types/database/generated/Session';
import { UserAuthenticationService } from '@/types/database/generated/User';

import Passkeys from './components/Passkeys';

// interface AuthenticationProps {}

const Authentication: FC = (): JSX.Element => {
    const [savingAuthenticationService, setSavingAuthenticationService] = useState<UserAuthenticationService | null>(
        null,
    );
    const [authorizedAuthenticationMethodsPolicy, setAuthorizedAuthenticationMethodsPolicy] =
        useState<WorkspaceAuthorizedAuthenticationMethods | null>(null);
    const [loadingAuthorizedAuthenticationMethods, setLoadingAuthorizedAuthenticationMethods] = useState(false);

    const { selectedUser: user, updateUser } = useUserStore();
    const { selectedWorkspace: workspace } = useWorkspaceStore();

    const GoogleSVG = () => (
        <svg
            width='16'
            height='16'
            viewBox='0 0 256 262'
            xmlns='http://www.w3.org/2000/svg'
            preserveAspectRatio='xMidYMid'
        >
            <path
                d='M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622 38.755 30.023 2.685.268c24.659-22.774 38.875-56.282 38.875-96.027'
                fill='#4285F4'
            />
            <path
                d='M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055-34.523 0-63.824-22.773-74.269-54.25l-1.531.13-40.298 31.187-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1'
                fill='#34A853'
            />
            <path
                d='M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82 0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602l42.356-32.782'
                fill='#FBBC05'
            />
            <path
                d='M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0 79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251'
                fill='#EB4335'
            />
        </svg>
    );

    const MicrosoftSVG = () => (
        <svg
            xmlns='http://www.w3.org/2000/svg'
            width='16'
            height='16'
            viewBox='0 0 256 262'
            preserveAspectRatio='xMidYMid'
        >
            <path fill='#F1511B' d='M121.666 121.666H0V0h121.666z' />
            <path fill='#80CC28' d='M256 121.666H134.335V0H256z' />
            <path fill='#00ADEF' d='M121.663 256.002H0V134.336h121.663z' />
            <path fill='#FBBC09' d='M256 256.002H134.335V134.336H256z' />
        </svg>
    );

    const GoogleIcon = (props: Partial<CustomIconComponentProps>) => <Icon component={GoogleSVG} {...props} />;
    const MicrosoftIcon = (props: Partial<CustomIconComponentProps>) => <Icon component={MicrosoftSVG} {...props} />;

    const { token } = useAntDesignTheme();

    const authenticationMethodOptions: Array<{
        description: string;
        icon: ReactNode;
        label: string;
        loginMethod: SessionLoginMethod;
        service: UserAuthenticationService;
    }> = [
        {
            description: 'Uses Google as the default sign-in method for this account.',
            icon: <GoogleIcon />,
            label: 'Google',
            loginMethod: SessionLoginMethod.GOOGLE,
            service: UserAuthenticationService.GOOGLE,
        },
        {
            description: 'Uses Microsoft as the default sign-in method for this account.',
            icon: <MicrosoftIcon />,
            label: 'Microsoft',
            loginMethod: SessionLoginMethod.MICROSOFT,
            service: UserAuthenticationService.MICROSOFT,
        },
        {
            description: 'Uses Email Magic Links as the default sign-in method for this account.',
            icon: <TbMailSpark size='1rem' />,
            label: 'Magic Link',
            loginMethod: SessionLoginMethod.MAGIC,
            service: UserAuthenticationService.MAGIC,
        },
        {
            description: "Uses your organization's SAML SSO connection as the default sign-in method for this account.",
            icon: <TbCloudLock size='1rem' />,
            label: 'SAML SSO',
            loginMethod: SessionLoginMethod.SSO,
            service: UserAuthenticationService.SSO,
        },
    ];

    const fetchAuthorizedAuthenticationMethods = useCallback(async () => {
        if (!workspace?.id) {
            setAuthorizedAuthenticationMethodsPolicy(null);

            return;
        }

        try {
            setLoadingAuthorizedAuthenticationMethods(true);

            const response = await workspaceService.getAuthorizedAuthenticationMethods(workspace.id);

            setAuthorizedAuthenticationMethodsPolicy(response);
        } catch (error) {
            captureException(error);
            setAuthorizedAuthenticationMethodsPolicy(null);
        } finally {
            setLoadingAuthorizedAuthenticationMethods(false);
        }
    }, [workspace?.id]);

    useEffect(() => {
        void fetchAuthorizedAuthenticationMethods();
    }, [fetchAuthorizedAuthenticationMethods]);

    const activeAuthorizedAuthenticationMethodsPolicy = useMemo<WorkspaceAuthorizedAuthenticationMethods | null>(() => {
        if (authorizedAuthenticationMethodsPolicy) {
            return authorizedAuthenticationMethodsPolicy;
        }

        if (!workspace?.id) {
            return null;
        }

        return {
            availableMethods: [],
            enabled: Boolean(workspace.authorizedAuthenticationMethodsEnabled),
            methods: workspace.authorizedAuthenticationMethods ?? [],
            updatedAt: workspace.authorizedAuthenticationMethodsUpdatedAt,
            updatedByUserId: workspace.authorizedAuthenticationMethodsUpdatedByUserId,
        };
    }, [
        authorizedAuthenticationMethodsPolicy,
        workspace?.authorizedAuthenticationMethods,
        workspace?.authorizedAuthenticationMethodsEnabled,
        workspace?.authorizedAuthenticationMethodsUpdatedAt,
        workspace?.authorizedAuthenticationMethodsUpdatedByUserId,
        workspace?.id,
    ]);

    const isAuthenticationMethodAllowedByWorkspace = useCallback(
        (loginMethod: SessionLoginMethod) => {
            if (!activeAuthorizedAuthenticationMethodsPolicy?.enabled) {
                return true;
            }

            return activeAuthorizedAuthenticationMethodsPolicy.methods.includes(loginMethod);
        },
        [activeAuthorizedAuthenticationMethodsPolicy],
    );

    const renderAuthenticationMethodAvatar = (icon?: ReactNode) =>
        icon ? (
            <Avatar
                size={22}
                shape='square'
                style={{
                    background: token.colorBgContainer,
                    border: `1px solid ${token.colorBorder}`,
                    color: token.colorTextTertiary,
                    display: 'inline-flex',
                    verticalAlign: 'middle',
                }}
                icon={
                    <span
                        style={{
                            alignItems: 'center',
                            display: 'inline-flex',
                            height: '100%',
                            justifyContent: 'center',
                            width: '100%',
                        }}
                    >
                        {icon}
                    </span>
                }
            />
        ) : null;

    const currentAuthenticationMethod = authenticationMethodOptions.find(
        (option) => option.service === user?.authenticationService,
    );
    const currentAuthenticationMethodLabel =
        currentAuthenticationMethod?.label ?? user?.authenticationService ?? 'Not set';

    const updateAuthenticationMethod = async (service: UserAuthenticationService) => {
        const nextMethod = authenticationMethodOptions.find((option) => option.service === service);

        if (!nextMethod || user?.authenticationService === service) {
            return;
        }

        if (!isAuthenticationMethodAllowedByWorkspace(nextMethod.loginMethod)) {
            toast.error('Authentication method blocked by workspace', {
                description: `${nextMethod.label} is not currently authorized for this workspace.`,
            });

            return;
        }

        Modal.confirm({
            title: `Use ${nextMethod.label} as your default sign-in method?`,
            content:
                'This changes the default authentication method for your account. Make sure you can still access this account with the selected method before continuing.',
            okText: `Use ${nextMethod.label}`,
            cancelText: 'Cancel',
            centered: true,
            onOk: async () => {
                setSavingAuthenticationService(service);

                try {
                    const updatedUser = await updateUser({ authenticationService: service });

                    if (updatedUser?.authenticationService !== service) {
                        throw new Error('Authentication method was not updated.');
                    }

                    toast.success('Default authentication updated', {
                        description: nextMethod.label,
                    });
                } catch {
                    toast.error('Unable to update authentication method');
                } finally {
                    setSavingAuthenticationService(null);
                }
            },
        });
    };

    const authenticationMethodMenuItems: MenuProps['items'] = authenticationMethodOptions.map((option) => {
        const isCurrent = option.service === user?.authenticationService;
        const isAllowedByWorkspace = isAuthenticationMethodAllowedByWorkspace(option.loginMethod);

        return {
            key: option.service,
            icon: renderAuthenticationMethodAvatar(option.icon),
            label: (
                <span>
                    {option.label}
                    {isCurrent && (
                        <Tag color='default' style={{ marginLeft: '0.5rem', marginRight: 0 }}>
                            Default
                        </Tag>
                    )}
                    {!isAllowedByWorkspace && (
                        <Tag color='warning' style={{ marginLeft: '0.5rem', marginRight: 0 }}>
                            Blocked by Workspace
                        </Tag>
                    )}
                </span>
            ),
            disabled:
                isCurrent ||
                !!savingAuthenticationService ||
                loadingAuthorizedAuthenticationMethods ||
                !isAllowedByWorkspace,
        };
    });

    return (
        <>
            <Typography.Title id='settings-account-security-authentication' level={4}>
                <TbUserScan size='1rem' style={{ verticalAlign: '-0.15rem', marginRight: '0.25rem' }} />
                Preferred Authentication Method
            </Typography.Title>
            <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                Manage the default sign-in method for this account
                <SmallTooltip
                    title='Changing your authentication method automatically disconnects the previous method.'
                    placement='right'
                >
                    <TbInfoCircle style={{ marginLeft: '0.25rem', verticalAlign: '-0.125rem' }} />
                </SmallTooltip>
            </Typography.Paragraph>
            <Dropdown
                trigger={['click']}
                placement='bottomRight'
                popupRender={() => (
                    <DropdownMenuPanel
                        {...{
                            items: authenticationMethodMenuItems,
                            onClick: ({ key }) => updateAuthenticationMethod(key as UserAuthenticationService),
                        }}
                    />
                )}
            >
                <DropdownButton
                    block
                    disabled={!user?.id}
                    loading={!!savingAuthenticationService}
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <span style={{ alignItems: 'center', display: 'inline-flex' }}>
                        {renderAuthenticationMethodAvatar(currentAuthenticationMethod?.icon)}
                        <span style={{ marginLeft: '0.5rem' }}>{currentAuthenticationMethodLabel}</span>
                    </span>
                </DropdownButton>
            </Dropdown>
            <Typography.Paragraph style={{ color: token.colorTextTertiary, marginTop: '0.75rem' }}>
                {currentAuthenticationMethod?.description ??
                    'Choose the default sign-in method for this account from the available options.'}
            </Typography.Paragraph>
            <Divider />
            <Passkeys />
        </>
    );
};

export default Authentication;
